import { useEffect, useMemo, useState, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import {
  reloadForActivatedUpdate,
  reloadWithoutDevTestParams,
  removeDevTestParamsFromHistory,
  removeOneShotReloadParamFromHistory,
  getDevTestMode,
  getPWAUpdateStorageKey,
} from '@/components/modals/pwa-update-modal-helpers'
import { PWAUpdateContext, type ApplyUpdateOptions } from './pwa-update-context'

export function PWAUpdateProvider({ children }: { children: ReactNode }) {
  const {
    needRefresh: [workerUpdateAvailable],
    updateServiceWorker,
  } = useRegisterSW({ onNeedReload: reloadForActivatedUpdate })
  const devTestMode = useMemo(() => getDevTestMode(), [])
  const updateAvailable = workerUpdateAvailable || devTestMode !== null
  const [isApplying, setIsApplying] = useState(false)
  const [applyFailureCount, setApplyFailureCount] = useState(0)
  const { trackEvent } = useAnalytics()

  useEffect(() => {
    removeOneShotReloadParamFromHistory()
  }, [])

  const applyUpdate = async ({ source, policyStateVersion = APP_VERSION, simulateIfNoWorker = devTestMode !== null }: ApplyUpdateOptions) => {
    if (isApplying || (!workerUpdateAvailable && !simulateIfNoWorker)) return

    trackEvent(AnalyticsEvent.AppUpdateRefreshClicked, { appVersion: APP_VERSION, appUpdateSource: source })
    setIsApplying(true)

    await new Promise((resolve) => setTimeout(resolve, 750))

    if (simulateIfNoWorker && !workerUpdateAvailable) {
      reloadWithoutDevTestParams()
      return
    }

    if (simulateIfNoWorker) removeDevTestParamsFromHistory()

    try {
      localStorage.setItem(getPWAUpdateStorageKey(policyStateVersion, 'lastApplyAttemptAt'), String(Date.now()))
      await updateServiceWorker()
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: 'pwa-update', appVersion: APP_VERSION } })
      setApplyFailureCount((count) => count + 1)
    } finally {
      setIsApplying(false)
    }
  }

  return <PWAUpdateContext.Provider value={{ updateAvailable, isApplying, applyFailureCount, applyUpdate }}>{children}</PWAUpdateContext.Provider>
}
